import { DbRef, SampleStatus } from '@caresend/types';
import { getProcedurePath, arrayToObj, hasStatus } from '@caresend/utils';
import update from 'immutability-helper';
import cloneDeep from 'lodash.clonedeep';
import { getChartingItemByIndexArray, getUpdatedChartingItems } from '../helpers/charting.js';
import { usePortalStore } from '../index.js';
import { useDbSyncStore } from '../../storeUtilities/dbSync/dbSyncStore.js';

/** PROCEDURE RELATED */
const setPlaceIDOnSamples = (placeID, procedureSamples) => {
    const updatedSamples = Object.values(procedureSamples).map((sample) => {
        const sampleUpdate = {
            ...sample,
            placeID,
        };
        return sampleUpdate;
    });
    return arrayToObj(updatedSamples, 'id');
};
/**
 * Delete place reference from samples in a procedure
 */
const deletePlaceReferenceFromSamples = (procedureID) => {
    const portalStore = usePortalStore();
    const db = useDbSyncStore();
    const procedure = portalStore.procedures.get(procedureID);
    if (!(procedure === null || procedure === void 0 ? void 0 : procedure.samples))
        return;
    const samples = setPlaceIDOnSamples('', procedure.samples);
    db.queueNextUpdate(getProcedurePath(procedureID, 'samples'), 'delete place reference from samples', samples, DbRef.ITINERARIES);
    // Update the local state if necessary
    portalStore.procedures.update({
        [procedureID]: {
            ...procedure,
            samples: samples,
        },
    });
};
const setProcedureSampleStatus = (procedureID, sampleID, status, _procedure) => {
    var _a;
    const portalStore = usePortalStore();
    const procedure = _procedure !== null && _procedure !== void 0 ? _procedure : portalStore.procedures.get(procedureID);
    if (!procedure)
        return;
    const sample = cloneDeep((_a = procedure.samples) === null || _a === void 0 ? void 0 : _a[sampleID]);
    if (!sample)
        return;
    if (hasStatus(sample, [SampleStatus.CANCELED, SampleStatus.FAILED]))
        return;
    sample.status = status;
    if (status === SampleStatus.COMPLETED) {
        sample.dropOffTimestamp = Date.now();
    }
    portalStore.procedures.setChildOf(procedureID, 'samples', {
        ...procedure.samples,
        [sampleID]: sample,
    }, 'update samples');
};
const setProcedureSamplesStatuses = (procedureID, statuses) => {
    const portalStore = usePortalStore();
    const procedure = portalStore.procedures.get(procedureID);
    if (!procedure)
        return;
    const { samples } = procedure;
    if (!samples)
        return;
    Object.values(samples).forEach((sample) => {
        if (hasStatus(sample, [
            SampleStatus.CANCELED,
            SampleStatus.FAILED,
            SampleStatus.COMPLETED,
        ]))
            return;
        setProcedureSampleStatus(procedureID, sample.id, statuses);
    });
};
const setProcedureChartingItems = (params) => {
    var _a;
    const { procedureID, newPartialChartingItem, chartingItemIndexArray, newChartingChecklistItem, checklistItemIndex, } = params;
    const { procedures } = usePortalStore();
    const procedure = procedures.get(procedureID);
    if (!procedure)
        return;
    const chartingItems = (_a = procedure.charting) === null || _a === void 0 ? void 0 : _a.items;
    if (!chartingItems)
        return;
    const chartingItem = getChartingItemByIndexArray(chartingItems, chartingItemIndexArray);
    let newPartialItem = {};
    if (newChartingChecklistItem && checklistItemIndex !== undefined && chartingItem) {
        newPartialItem = update(chartingItem, {
            checklist: {
                [checklistItemIndex]: {
                    $merge: newChartingChecklistItem,
                },
            },
        });
    }
    else if (newPartialChartingItem) {
        newPartialItem = newPartialChartingItem;
    }
    const newItems = getUpdatedChartingItems(chartingItems, chartingItemIndexArray, newPartialItem);
    procedures.setChildOf(procedureID, 'charting/items', newItems, `Updating charting items for procedure ${procedureID}`);
};

export { deletePlaceReferenceFromSamples, setProcedureChartingItems, setProcedureSampleStatus, setProcedureSamplesStatuses };
